import React, { useEffect, useMemo, useState } from 'react';

import CloseIcon from '@material-ui/icons/Close';
import { FilledInput, FormControl, Grid, IconButton, InputAdornment, InputLabel } from '@material-ui/core';

import DefaultTable from '../../components/DefaultTable';
import PageWrapper from '../../components/PageWrapper';
import SaveBox from '../../components/SaveBox';

import { selectMultiplePermissionaryColumn, selectPermissionaryColumn } from '../../table-configs/columns-schemas';
import Search from '@material-ui/icons/Search';
import SimpleAlert from '../../components/SimpleAlert';
import companyEndpoints from '../../../../api/services/companyEndpoints';
import lineEndpoint from '../../../../api/services/lineEndpoint';
import { useTableReducer } from '../../hooks/useTableReducer';

const FindPermissionary = ({
    isCompany = false,
    onClose,
    onSave,
    permissionaries,
    requestParams = {},
    isMultiple = false,
}) => {
    const [selectedTempPermissionary, setSelectedTempPermissionary] = useState([]);
    const [loading, setLoading] = useState(false);

    const [orderBy, setOrderBy] = useState('deNomePermissionario');
    const [orderDirection, setOrderDirection] = useState('asc');
    const [searchTxt, setSearchTxt] = useState('');
    const [rows, setRows] = useState([]);
    const [alertMessage, setAlertMessage] = useState(null);

    const [tablePermissionaries, actions] = useTableReducer({ pageSize: 5 });
    const { changePage, changePageSize, resetTable, loadData: loadTablePermissionaries } = actions;

    const tableColumns = useMemo(() => {
        if (!isMultiple) return selectPermissionaryColumn;
        return selectMultiplePermissionaryColumn;
    }, [isMultiple]);

    useEffect(() => {
        (() => {
            if (!isMultiple) {
                setRows(tablePermissionaries?.content || []);
                return;
            }
            const tempPermissionaries = tablePermissionaries?.content || [];
            setRows(
                tempPermissionaries.map((permissionary) => {
                    const temp = { ...permissionary };
                    if (permissionaries) {
                        const found = permissionaries
                            .filter((p) => !p.boExcluido)
                            .find((p) => p.deCpfCnpj === permissionary.deCpfCnpj);
                        if (found) {
                            temp.checked = true;
                            temp.lockCheckbox = true;
                        }
                    }
                    return temp;
                }),
            );
        })();
        // eslint-disable-next-line
    }, [tablePermissionaries.content, permissionaries, isMultiple]);

    const [filters, setFilters] = useState(() => {
        return {
            ...requestParams,
            campoOrdenacao: orderBy,
            direcaoOrdenacao: orderDirection,
        };
    });

    const prepareCompany = (companiesData) => {
        let preparedData = [];
        if (companiesData && companiesData.length > 0) {
            preparedData = companiesData.map((company) => {
                return {
                    boAtivo: company.boSituacaoOrgao,
                    cdEmpresa: company.cdEmpresa,
                    deCpfCnpj: company.cdTipoPessoaFisica
                        ? company.deCpf || company.deCpfCnpj || ''
                        : company.deCnpjMatriz || '',
                    deNomePermissionario: company.cdTipoPessoaFisica
                        ? company.nmCompleto || ''
                        : company.deRazaoSocial || '',
                    permissionario: false,
                    tpCategoriaCnh: 'A',
                };
            });
        }
        return preparedData;
    };

    useEffect(() => {
        (async () => {
            setLoading(true);
            if (requestParams.onlyCompanies) {
                const params = {
                    page: tablePermissionaries.page,
                    size: tablePermissionaries.pageSize,
                    request: { ...filters },
                };
                const response = await companyEndpoints.getCompanies(params);
                if (response && response.status === 200) {
                    const resData = { ...response.data };
                    if (response.data.content.length === 0) {
                        resetTable();
                    } else {
                        resData.content = prepareCompany(resData.content);
                        loadTablePermissionaries(resData);
                    }
                }
            } else {
                const params = {
                    page: tablePermissionaries.page,
                    size: tablePermissionaries.pageSize,
                    request: { ...filters, boEmpresa: isCompany },
                };
                const response = await lineEndpoint.getPermissionaries(params);
                if (response.status === 200) {
                    const resData = response.data;
                    if (resData.content.length === 0) {
                        resetTable();
                    } else {
                        loadTablePermissionaries(resData);
                    }
                }
            }
            setLoading(false);
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tablePermissionaries.page, tablePermissionaries.pageSize, filters]);

    const handleSelectPermissionary = ({ data }) => {
        if (!isMultiple) {
            setSelectedTempPermissionary(data);
            return;
        }
        const tempRows = rows.map((row) => {
            const temp = { ...row };
            if (temp.cdLinhaPermissionario === data.cdLinhaPermissionario) {
                temp.checked = !temp.checked;
            }
            return temp;
        });
        setRows(tempRows);
        setSelectedTempPermissionary(tempRows.filter((p) => p.checked));
    };

    const handleSaveSelected = () => {
        if (onSave) {
            if (selectedTempPermissionary.length === 0) {
                setAlertMessage(`Selecione um ${isMultiple ? 'ou mais permissionários' : 'permissionário'}.`);
            } else {
                onSave(selectedTempPermissionary);
                onClose();
            }
        }
    };

    const handleChangePageSize = (e) => {
        changePage(0);
        changePageSize(parseInt(e.target.value));
    };

    const handleChangePage = (e, page) => {
        changePage(page);
    };

    const handleChangeOrder = (event, property) => {
        let tempDirection = orderDirection;
        if (property === orderBy) {
            tempDirection = orderDirection === 'desc' ? 'asc' : 'desc';
        }
        const temp = { ...filters };
        temp['campoOrdenacao'] = `${property}`;
        temp['direcaoOrdenacao'] = tempDirection;
        setOrderDirection(tempDirection);
        setOrderBy(property);
        setFilters(temp);
        handleChangePage(null, 0);
    };

    const handleTextSearch = (onBlur) => {
        if (onBlur) {
            if (searchTxt !== '') return;
        }
        handleChangeFilter('txtBusca', searchTxt);
        // setPage(0);
    };

    const handleChangeFilter = (key, value) => {
        const temp = { ...filters };
        temp[key] = value;
        setFilters(temp);
    };

    return (
        <>
            <PageWrapper
                maxWidth="md"
                title="Buscar permissionários"
                isPlain
                actionButton={
                    <IconButton onClick={onClose}>
                        <CloseIcon />
                    </IconButton>
                }>
                <Grid item xs={12} container spacing={2}>
                    <Grid item xs={4}>
                        <FormControl size="small" variant="filled" fullWidth>
                            <InputLabel>Buscar</InputLabel>
                            <FilledInput
                                id="outlined-adornment-weight"
                                value={searchTxt}
                                onChange={(e) => setSearchTxt(e.target.value)}
                                endAdornment={
                                    <InputAdornment
                                        position="end"
                                        onClick={() => handleTextSearch()}
                                        style={{ cursor: 'pointer' }}>
                                        <Search />
                                    </InputAdornment>
                                }
                                aria-describedby="outlined-weight-helper-text"
                                inputProps={{
                                    'aria-label': 'weight',
                                }}
                                onBlur={() => {
                                    handleTextSearch(true);
                                }}
                                onKeyPress={(ev) => {
                                    if (ev.key === 'Enter') {
                                        ev.preventDefault();
                                        handleTextSearch();
                                    }
                                }}
                            />
                        </FormControl>
                    </Grid>

                    <Grid item xs={12}>
                        <DefaultTable
                            loading={loading}
                            columns={tableColumns}
                            rows={rows}
                            entryProps={{
                                onChangeSelect: handleSelectPermissionary,
                                selectedId: selectedTempPermissionary?.deCpfCnpj,
                            }}
                            paginationProps={{
                                rowsPerPageOptions: [5, 10, 25],
                                count: tablePermissionaries.count,
                                rowsPerPage: tablePermissionaries.pageSize,
                                page: tablePermissionaries.page,
                                onPageChange: handleChangePage,
                                onRowsPerPageChange: handleChangePageSize,
                            }}
                            orderBy={orderBy}
                            order={orderDirection}
                            onRequestSort={handleChangeOrder}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <SaveBox
                            firstButtonLabel={'VOLTAR'}
                            middleButtonLabel="Adicionar"
                            onSave={handleSaveSelected}
                            onCancel={onClose}
                        />
                    </Grid>
                </Grid>
            </PageWrapper>
            <SimpleAlert open={!!alertMessage} onClose={() => setAlertMessage(null)}>
                {alertMessage}
            </SimpleAlert>
        </>
    );
};

export default FindPermissionary;
